/**
 * Typed configuration accessor
 * Phase C: Config Cleanup
 *
 * Single import point for everything in data.json.
 * Provides:
 *  1. A strongly-typed constant for every top-level key
 *  2. Convenience getters that take a crop/region and resolve the right defaults
 *
 * Usage:
 *   import { appConfig, getDefaultFilters } from '@/config/appConfig';
 *   const roles = appConfig.allRoles;
 *   const filters = getDefaultFilters('EAME', 'Corn');
 */

import defaultConfig from './data.json';
import type {
  IRegionConfig,
  ICropConfig,
  ICropSeasonConfig,
  IBrandConfig,
  IModeConfig,
  IYieldUnitConfig,
  IFilterParam,
  IFilterViewSection,
  IMapThreshold,
  ISummaryItemConfig,
  ICropFeatureConfig,
  IRegionFeatureConfig
} from '../types';

// ─── Raw typed references (tree-shakeable) ──────────────────────────

const cfg = defaultConfig as Record<string, unknown>;

export const appConfig = {
  agreementUrl: cfg.agreementUrl as string,
  userManual: cfg.userManual as string,
  faqUrl: cfg.faqUrl as string,
  teamChannelUrl: cfg.teamChannelUrl as string,
  allRoles: cfg.allRoles as string[],
  allYieldUnits: cfg.allYieldUnits as IYieldUnitConfig[],
  allBrands: cfg.allBrands as IBrandConfig[],
  allModes: cfg.allModes as IModeConfig[],
  allModules: cfg.allModules as Array<{ id: string; name: string }>,
  allRegions: cfg.allRegions as IRegionConfig[],
  themes: cfg.themes as Array<{ id: string; name: string }>,
  allCrops: cfg.allCrops as ICropConfig[],
  allCropSeasons: cfg.allCropSeasons as ICropSeasonConfig[],
  allCountry: cfg.allCountry as Array<{ Region: string; Crop: string; Country: string }>,
  userCountry: cfg.userCountry as string[],
  entryPerformance: cfg.entryPerformance as Record<string, unknown>,
  defaultChartConfig: cfg.defaultChartConfig as Array<{
    title: string;
    key: string;
    mode: string[];
    isOrthogonalMode: boolean;
    crop: string[];
    region: string[];
    content: unknown;
  }>,
  defaultFilterView: cfg.defaultFilterView as Record<string, Record<string, IFilterViewSection>>,
  defaultMapThreshHolds: cfg.defaultMapThreshHolds as Record<string, IMapThreshold[]>,
  defaultBreakoutCards: cfg.defaultBreakoutCards as Array<{
    key: string;
    title: string;
    label?: string;
    type?: string;
    crop: string[];
    region: string[];
  }>,
  regionThresholds: cfg.regionThresholds as Record<string, IMapThreshold[]>,
  cropConfig: cfg.cropConfig as Record<string, ICropFeatureConfig>,
  regionConfig: cfg.regionConfig as Record<string, IRegionFeatureConfig>
} as const;

// ─── Convenience getters ────────────────────────────────────────────

/**
 * Get the default selected filters for a given region + crop key.
 * Falls back to empty object if not found.
 */
export function getDefaultFilters(region: string, crop: string): Record<string, IFilterParam> {
  const ep = appConfig.entryPerformance as Record<string, unknown>;
  const key = `${region.toUpperCase()}_${crop.toUpperCase()}`;
  return (ep?.defaultSelectedFilters as Record<string, Record<string, IFilterParam>>)?.[key] ?? {};
}

/**
 * Get the default filter view for a region+crop.
 */
export function getDefaultFilterView(
  region: string,
  crop: string
): Record<string, IFilterViewSection> {
  const key = `${region.toUpperCase()}_${crop.toUpperCase()}`;
  return (
    ((appConfig.defaultFilterView as Record<string, unknown>)?.[key] as Record<
      string,
      IFilterViewSection
    >) ?? {}
  );
}

/**
 * Get yield units applicable to a crop + region.
 */
export function getYieldUnitsForCropRegion(crop: string, region: string): IYieldUnitConfig[] {
  return appConfig.allYieldUnits.filter(
    (u) => u.crop.includes(crop.toLowerCase()) && u.region.includes(region.toLowerCase())
  );
}

/**
 * Get pairwise legends for a specific yield unit in a crop/region.
 */
export function getPairwiseLegends(
  crop: string,
  region: string,
  yieldUnit: string = 't/ha'
): Array<{ color: string; display: string }> {
  const units = getYieldUnitsForCropRegion(crop, region);
  return units.find((u) => u.name === yieldUnit)?.pairwiseLegends ?? [];
}

/**
 * Get brands for a given region.
 */
export function getBrandsForRegion(region: string): IBrandConfig[] {
  return appConfig.allBrands.filter((b) => b.region && b.region.includes(region));
}

/**
 * Get countries for a given region + crop.
 */
export function getCountriesForRegionCrop(region: string, crop: string): string[] {
  return appConfig.allCountry
    .filter(
      (c) =>
        c.Region.toUpperCase() === region.toUpperCase() &&
        c.Crop.toUpperCase() === crop.toUpperCase()
    )
    .map((c) => c.Country);
}

/**
 * Get crop seasons applicable to a crop + region.
 */
export function getCropSeasons(crop: string, region: string): ICropSeasonConfig[] {
  return appConfig.allCropSeasons.filter(
    (s) =>
      s.crop.some((c) => c.toLowerCase() === crop.toLowerCase()) &&
      s.region.some((r) => r.toLowerCase() === region.toLowerCase())
  );
}

/**
 * Get map thresholds for a crop.
 */
export function getMapThresholds(crop: string): IMapThreshold[] {
  return appConfig.defaultMapThreshHolds?.[crop] ?? [];
}

/**
 * Whether the Mst+/- (moisture diff) column should be shown in pairwise tables/exports.
 * Reads from cropConfig.showMstColumn — defaults to true if not configured.
 */
export function getShowMstColumn(crop: string): boolean {
  if (!crop) return true;
  return appConfig.cropConfig[crop.toLowerCase()]?.showMstColumn ?? true;
}

/**
 * Build a global map from raw filterKey → i18n filterName (PA_ key)
 * by walking ALL sections / crops / regions in defaultFilterView.
 * Cached after first call since data.json is static at runtime.
 */
let _filterKeyI18nMap: Record<string, string> | null = null;

export function getFilterKeyToI18nMap(): Record<string, string> {
  if (_filterKeyI18nMap) return _filterKeyI18nMap;

  const map: Record<string, string> = {};
  const dfv = appConfig.defaultFilterView as Record<string, unknown>;

  // Walk every section (experiment, location, performance, …)
  for (const sectionKey of Object.keys(dfv)) {
    const section = dfv[sectionKey] as Record<string, unknown> | undefined;
    const attrs = section?.attributes as Record<string, unknown> | undefined;
    if (!attrs || typeof attrs !== 'object') continue;

    // Walk every crop/region combo inside the section
    for (const comboKey of Object.keys(attrs)) {
      const filterItems = attrs[comboKey] as Array<{ filterKey?: string; filterName?: string }>;
      if (!Array.isArray(filterItems)) continue;

      for (const item of filterItems) {
        if (item?.filterKey && item?.filterName) {
          // First-write wins — keeps the most generic mapping
          if (!map[item.filterKey]) {
            map[item.filterKey] = item.filterName;
          }
        }
      }
    }
  }

  _filterKeyI18nMap = map;
  return map;
}

/**
 * Resolve a filter title to an i18n key.
 * If the title is already a PA_ i18n key it passes through unchanged.
 * Otherwise looks up the raw filterKey in the global map.
 */
export function resolveFilterTitle(title: string): string {
  if (!title) return title;
  // Already an i18n key — no lookup needed
  if (title.startsWith('PA_')) return title;
  const map = getFilterKeyToI18nMap();
  return map[title] ?? title;
}

/**
 * Get summary section config items for a specific crop.
 */
export function getSummaryConfig(crop: string): ISummaryItemConfig[] {
  const ep = appConfig.entryPerformance as Record<string, unknown>;
  const summary = ep?.summarySection as ISummaryItemConfig[] | undefined;
  if (!summary) return [];
  return summary.filter(
    (item) => !item.crop || item.crop.length === 0 || item.crop.includes(crop.toLowerCase())
  );
}

// ─── Crop / Region Feature Config Accessors ─────────────────────────

/** Default crop feature config when crop is not found in cropConfig. */
const DEFAULT_CROP_FEATURE: ICropFeatureConfig = {
  supportsTprFilter: false,
  priceGuideType: null,
  supportsCustomThreshold: false,
  showRmFilter: false,
  showMinLocations: true,
  defaultMinLocations: 8
};

/** Default region feature config when region is not found in regionConfig. */
const DEFAULT_REGION_FEATURE: IRegionFeatureConfig = {
  apiFormat: 'json',
  apiVersion: 'v1',
  hasTieColumn: false,
  supportsCustomThreshold: false,
  showRmFilter: true,
  showMinLocations: true,
  defaultMinLocations: 8
};

/**
 * Get the feature config for a specific crop.
 * Falls back to a safe default if the crop is not configured.
 */
export function getCropFeatureConfig(crop: string): ICropFeatureConfig {
  return appConfig.cropConfig[crop.toLowerCase()] ?? DEFAULT_CROP_FEATURE;
}

/**
 * Get the feature config for a specific region.
 * Falls back to a safe default if the region is not configured.
 */
export function getRegionFeatureConfig(region: string): IRegionFeatureConfig {
  return appConfig.regionConfig[region.toUpperCase()] ?? DEFAULT_REGION_FEATURE;
}

/**
 * Whether the crop supports TPR (Temperature Precipitation) filter type.
 * Currently true for corn and cornsilage.
 */
export function supportsTprFilter(crop: string): boolean {
  return getCropFeatureConfig(crop).supportsTprFilter;
}

/**
 * Whether the crop supports custom threshold mode.
 */
export function supportsCustomThreshold(crop: string): boolean {
  return getCropFeatureConfig(crop).supportsCustomThreshold;
}

/**
 * Get the price guide rendering type for a crop.
 * Returns 'chart', 'table', or null (no price guide).
 */
export function getPriceGuideType(crop: string): 'chart' | 'table' | null {
  return getCropFeatureConfig(crop).priceGuideType;
}

/**
 * Whether a crop has seasons for a given region.
 * Uses the existing allCropSeasons config.
 */
export function hasCropSeasons(crop: string, region: string): boolean {
  return getCropSeasons(crop, region).length > 0;
}

/**
 * Whether a crop is configured in cropConfig (i.e. known to the system).
 * Returns false for null, undefined, empty, or unrecognised crop names.
 */
export function isKnownCrop(crop: string | null | undefined): boolean {
  return !!crop && appConfig.cropConfig[crop.toLowerCase()] !== undefined;
}

/** Default company name used across the app. */
export const DEFAULT_COMPANY = 'All';
